import "./Preview.scss";
import { createMailBody, useClickOutside } from "@/utils";
import { Contact, InvoicesHistoryData } from "@/types";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PdfDocument from "../pdf/Pdf";
import { invoicesRepository } from "@/database/repositories/invoicesRepository";
import { ChevronDown, ChevronLeft, Copy, Send, TriangleAlert, User } from "lucide-react";
import { useForm } from "react-hook-form";
import { PreviewInvoice } from "@/shared/previewInvoice/PreviewInvoice";
import { Flip, toast, ToastContainer } from "react-toastify";
import { useNetworkStatus } from "@/hooks/networkStatus";
import { mainApi } from "@/api/api";
import { CSSTransition } from "react-transition-group";
import { contactsRepository } from "@/database/repositories/contactsRepository";
import { useDb } from "@/database/provider/useDb";

export const Preview = () => {
    const navigate = useNavigate();
    const db = useDb();
    const { state } = useLocation();
    const isOnline = useNetworkStatus();
    const [searchParams] = useSearchParams();
    const [previewConfig, setPreviewConfig] = useState<InvoicesHistoryData>();
    const [prevLocation, setPrevLocation] = useState<string>("");
    const [isSendFormOpen, setIsSendFormOpen] = useState(false);
    const sendLinkRef = useRef<HTMLAnchorElement | null>(null);
    const [selectedEmail, setSelectedEmail] = useState<string>();
    const [contacts, setContacts] = useState<Contact[]>();
    const [isContactListOpen, setIsContactsListOpen] = useState(false);

    const { register, watch, setValue, handleSubmit, formState: { errors } }
        = useForm<{ message: string, subject: string }>();
    const listRef = useRef(null);

    const subject = watch("subject");
    const message = watch("message");

    async function handleOpenSendForm() {
        try {
            if (previewConfig) {
                const { publicToken, privateToken } = previewConfig;
                if (isOnline && !publicToken && !privateToken) {
                    const { publicToken, privateToken } = await invoicesRepository.update(db, {
                        ...previewConfig,
                        privateToken: Date.now().toString(36) + Math.random().toString(36).substring(2),
                        publicToken: Date.now().toString(36) + Math.random().toString(36).substring(2),
                        customerId: previewConfig.customerId as number,
                        contactId: previewConfig.contactId as number,
                        lastModified: new Date(),
                        invoiceData: previewConfig
                    })
                    const cfg = { ...previewConfig, publicToken, privateToken };
                    mainApi.addInvoice(cfg);
                    setPreviewConfig(cfg);
                    setValue("message", createMailBody(cfg, `${window.location.origin}/invoice/${cfg.publicToken}`));
                }
                if (isOnline && publicToken && privateToken) {
                    mainApi.updateInvoice(previewConfig);
                }
                if (!isOnline && publicToken && privateToken) {
                    toast.warning('Shared invoice was not updated! You are in offline mode.', {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: true,
                        closeOnClick: false,
                        pauseOnHover: true,
                        theme: "light",
                        transition: Flip,
                    });
                }
                if (!isOnline && !publicToken && !privateToken) {
                    toast.error('Shared invoice unavailable! You are in offline mode.', {
                        position: "top-right",
                        autoClose: 5000,
                        hideProgressBar: true,
                        closeOnClick: false,
                        pauseOnHover: true,
                        theme: "light",
                        transition: Flip,
                    });
                }
                setIsSendFormOpen(true);
            }
        }
        catch (err: any) {
            throw new Error(err.message)
        }
    }

    useClickOutside(listRef, () => {
        setIsContactsListOpen(false);
    }, "send-email__contact-button");

    useEffect(() => {
        try {
            (async () => {
                const template = searchParams.get("template");
                if (state) {
                    const { prevPath } = state;
                    setPrevLocation(prevPath.split("?")[0]);
                }

                if (template) {
                    const invoice = await invoicesRepository.getById(db, template);
                    setPreviewConfig(invoice);

                    setValue("subject", `${invoice.billTo ?? invoice.billTo} Invoice #${invoice.invoiceNumber} from ${invoice.billFrom}`);
                    setValue("message", createMailBody(invoice, `${window.location.origin}/invoice/${invoice.publicToken}`));
                    const contacts = await contactsRepository.getCustomerContacts(db, invoice.customerId as number);
                    setContacts(contacts);
                    setSelectedEmail(invoice.billToContact.email);
                }
                else navigate("/");
            })();
        }
        catch (err: any) {
            throw new Error(err.message);
        }
    }, [navigate, setValue, searchParams, db, state]);

    return (
        <section className="preview">
            <ToastContainer />
            {previewConfig && <div className="preview__container">
                {isSendFormOpen ? <form className="send-email" onSubmit={handleSubmit(() => {
                    sendLinkRef.current?.click();
                })}>
                    <div className="send-email__head">
                        <button className="send-email__back" type="button" onClick={() => {
                            setIsSendFormOpen(false);
                        }}>
                            <ChevronLeft width={18} height={18} />
                        </button>
                        <button className="send-email__send" type="submit" disabled={Object.keys(errors).length > 0}>
                            <Send width={14} height={14} /> Send
                        </button>
                        <Link className="send-email__send-link" ref={sendLinkRef}
                            to={`mailto:${selectedEmail}?subject=${subject}&body=${encodeURIComponent(message)}`}>
                            Continue
                        </Link>
                    </div>
                    <div className="send-email__elements">
                        <div className="send-email__send-to">
                            <span className="send-email__label">Send to:</span>
                            <button className="send-email__contact-button" type="button" onClick={() => {
                                setIsContactsListOpen(!isContactListOpen);
                            }}>
                                <b>{selectedEmail}</b>
                                <ChevronDown width={14} height={14} />
                            </button>

                            <CSSTransition
                                in={isContactListOpen && contacts && contacts.length > 1}
                                timeout={200}
                                classNames="fade"
                                exit={false}
                                unmountOnExit>
                                <div className="send-email__contacts-list" ref={listRef}>
                                    {contacts?.map(({ email }, i) => <ul className="send-email__contact" key={i}>
                                        <button type="button" onClick={() => {
                                            setSelectedEmail(email);
                                            setIsContactsListOpen(false);
                                        }}><User width={13} height={13} />{email}</button>
                                    </ul>)}
                                </div>
                            </CSSTransition>
                        </div>
                        <div className="send-email__form-item">
                            <label className="send-email__label">Subject</label>
                            <input {...register("subject", { required: true })} autoComplete="off" />
                        </div>
                        <div className="send-email__form-item">
                            <label className="send-email__label">Message</label>
                            <textarea {...register("message", { required: true })} autoComplete="off" />
                        </div>
                        {(previewConfig.privateToken && previewConfig.publicToken) ?
                            <div className="send-email__share-link-container">
                                <input className="send-email__share-link"
                                    value={`${window.location.origin}/invoice/${previewConfig.publicToken}`}
                                    readOnly />
                                <button type="button" onClick={() => {
                                    toast.success('Invoice link copied!', {
                                        position: "top-right",
                                        autoClose: 5000,
                                        hideProgressBar: true,
                                        closeOnClick: false,
                                        pauseOnHover: true,
                                        theme: "light",
                                        transition: Flip,
                                    });
                                    navigator.clipboard.writeText(`${window.location.origin}/invoice/${previewConfig.publicToken}`);
                                }}>
                                    <Copy width={16} height={16} />
                                </button>
                            </div> : <div className="send-email__warning">
                                <span>
                                    <TriangleAlert width={13} height={13} />
                                    Turn on the internet to get the invoice link.
                                </span>
                            </div>}
                    </div>
                </form> :
                    <div className="preview-controls">
                        {(prevLocation === "/history" || prevLocation === "/transactions" || prevLocation === "/archive/invoices") &&
                            <Link className="preview-controls__back" type="button" to={`${state.prevPath}`}>
                                Back
                            </Link>}
                        <Link className="preview-controls__edit" to={{
                            pathname: "/",
                            search: `template=${previewConfig.invoiceNumber}&edit=true`
                        }}>
                            Edit
                        </Link>
                        <button className="preview-controls__edit" onClick={handleOpenSendForm}>Send</button>
                        <PDFDownloadLink
                            className="preview-controls__download"
                            document={<PdfDocument data={previewConfig.invoiceData} />}
                            fileName={`Invoice #${previewConfig.invoiceNumber}.pdf`}
                        >
                            Download invoice
                        </PDFDownloadLink>
                    </div>}
                <PreviewInvoice previewConfig={previewConfig.invoiceData} />
            </div>}
            {/*
            <PDFViewer>
                <PdfDocument data={data} />
            </PDFViewer>
             */}
        </section >
    )
}

